import { Link } from 'react-router-dom';

import logo from '@dmp/assets/icons/logo-colorful.svg';
import { ROUTE_PATHS } from '@dmp/constant';
import { useGlobalStore } from '@dmp/stores/globalStore';
import clsx from 'clsx';

import { LoggedInActions } from './components/LoggedInActions';
import { NavigationLinks } from './components/NavigationLinks';
import { NonLoggedInActions } from './components/NonLoggedInActions';
import './GlobalHeader.css';
import type { GlobalHeaderProps } from './GlobalHeader.types';

/**
 * Global Header Component
 *
 * Displays the main navigation bar with:
 * - Logo (always visible)
 * - Navigation links (Agency Catalog for Centific users, Expert Data Service, Partners, Contact Us)
 * - Logged-in actions (Datahub link and user dropdown when authenticated)
 *
 * Implements Stories:
 * - DMP - Redesign UI - Streamlined Navigation Bar for All Users
 * - DMP - Redesign UI - Agency Catalog Access for Internal Users
 */
export const GlobalHeader = ({ className }: GlobalHeaderProps) => {
  const isLoggedIn = useGlobalStore((s) => s.isLoggedIn);
  const userInfo = useGlobalStore((s) => s.userInfo);

  return (
    <div
      id="header"
      className={clsx(
        'flex flex-wrap md:flex-nowrap justify-between items-center py-[14px] px-3 md:px-6 bg-white rounded-[10px] mt-[12px] mb-[22px] shadow-[0px_0px_20px_0px_rgba(143,143,144,0.17)] gap-y-3',
        className
      )}
    >
      {/* Logo - Mobile: Full width centered, Desktop: Left aligned */}
      <Link
        to={ROUTE_PATHS.LANDING_PAGE}
        className="brand-logo flex-shrink-0 w-full md:w-auto flex justify-center md:justify-start"
      >
        <img src={logo} alt="logo" className="h-[32px] md:h-[40px]" />
      </Link>

      {/* Navigation Links - Mobile: Full width row 2, Desktop: Centered */}
      <div className="nav-scroll-container w-full md:flex-1 overflow-x-auto overflow-y-hidden md:mx-2 md:min-w-0 flex items-center justify-center order-2">
        <NavigationLinks isLoggedIn={isLoggedIn} userEmail={userInfo?.user_email} />
      </div>

      {/* Right Section - Mobile: Full width row 3 centered, Desktop: Right aligned */}
      {isLoggedIn && (
        <div className="flex-shrink-0 w-full md:w-auto md:min-w-[150px] lg:min-w-[200px] flex justify-center md:justify-end order-3">
          <LoggedInActions />
        </div>
      )}

      {/* Empty spacer for non-logged-in users on desktop */}
      {!isLoggedIn && <NonLoggedInActions />}
    </div>
  );
};
